// Section 22 Report overlay at season start: deaths by name, age and cause with the three
// contributing factors (set in the same type as everything else, never highlighted), births,
// illness and recoveries, crimes and justice, events with links to their prompts, production
// versus last season, shortages with who went short, warnings, the steady-state result at year
// end, and the outcome with a way to the Ledger. Focus lands on Continue; Enter continues.
import type { ComponentChildren } from 'preact';
import type { JobId, PromptId } from '../../sim/api';
import type { ReportVM } from '../../viewModels';
import { capitalise, humanize, oneDecimal, seasonName, signed } from '../format';
import { causeLabel, resourceLabel, uiText } from '../text/en';
import { narrationLine } from '../shell/text';
import { Num } from '../tooltip/num';
import './report.css';

interface Props {
  vm: ReportVM;
  ended: boolean;
  jobLabel: (job: JobId) => string;
  onContinue: () => void;
  onAnswer: (id: PromptId) => void;
  onLedger: () => void;
  onTitle: () => void;
}

function Section({ id, title, children }: { id: string; title: string; children: ComponentChildren }) {
  return (
    <section class="report__section" aria-labelledby={`report-${id}`}>
      <h3 id={`report-${id}`}>{title}</h3>
      {children}
    </section>
  );
}

function Lines({ lines }: { lines: ReportVM['health'] }) {
  return (
    <ul class="prose-list">
      {lines.map((n, i) => (
        <li key={i}>{narrationLine(n)}</li>
      ))}
    </ul>
  );
}

export function ReportOverlay({ vm, ended, onContinue, onAnswer, onLedger, onTitle }: Props) {
  const t = uiText.report;
  const outcome = vm.outcome;
  return (
    <article class="report">
      <h2 id="report-title">{t.title(seasonName(vm.season), vm.year)}</h2>
      <p class="hint">
        {t.population(vm.population.start, vm.population.end)}
      </p>
      {outcome !== null && (
        <section class="report__outcome" aria-labelledby="report-outcome">
          <h3 id="report-outcome">{outcome.kind === 'won' ? t.outcomeWon : t.outcomeLost}</h3>
          {outcome.kind === 'lost' && <p>{capitalise(t.lossReason[outcome.reason] ?? outcome.reason)}.</p>}
        </section>
      )}
      {vm.deaths.length > 0 && (
        <Section id="deaths" title={t.deaths}>
          <ul class="prose-list report__deaths">
            {vm.deaths.map((d) => (
              <li key={d.personId}>
                {t.death(
                  d.name,
                  d.ageYears,
                  d.illnessId === null ? causeLabel[d.cause] : humanize(d.illnessId).toLowerCase(),
                  d.jobLabel.toLowerCase(),
                )}
                {d.factors.length > 0 && (
                  <ul class="report__factors" aria-label={t.factors}>
                    {d.factors.map((f, i) => (
                      <li key={i}>
                        {f.note ?? humanize(f.source)}
                        {f.op !== 'base' && f.delta !== 0 && <span class="num"> ({signed(f.delta)})</span>}
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        </Section>
      )}
      {vm.births.length > 0 && (
        <Section id="births" title={t.births}>
          <ul class="prose-list">
            {vm.births.map((b) => (
              <li key={b.personId}>{t.born(b.name, b.mother)}</li>
            ))}
          </ul>
        </Section>
      )}
      {vm.health.length > 0 && (
        <Section id="health" title={t.health}>
          <Lines lines={vm.health} />
        </Section>
      )}
      {vm.crime.length > 0 && (
        <Section id="crime" title={t.crime}>
          <Lines lines={vm.crime} />
        </Section>
      )}
      {(vm.events.length > 0 || vm.prompts.length > 0) && (
        <Section id="events" title={t.events}>
          <Lines lines={vm.events} />
          {vm.prompts.length > 0 && (
            <>
              <h4>{t.prompts}</h4>
              <ul class="report__prompts">
                {vm.prompts.map((p) => (
                  <li key={p.id}>
                    {t.promptKind[p.kind] ?? p.kind}: {humanize(p.subject)}{' '}
                    {p.answered ? (
                      <span class="hint">({t.answered})</span>
                    ) : null}
                    {!ended && (
                      <button type="button" class="button button--small" onClick={() => onAnswer(p.id)}>
                        {t.answer}
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </>
          )}
        </Section>
      )}
      {vm.shortages.length > 0 && (
        <Section id="shortages" title={t.shortages}>
          <ul class="prose-list">
            {vm.shortages.map((s) => (
              <li key={s.resource}>
                {t.shortageLine(resourceLabel[s.resource], s.covered, s.population, oneDecimal(s.shortfall))}
                {s.whoShort.length > 0 && (
                  <span class="hint">
                    {' '}
                    {t.whoShort}:{' '}
                    {s.whoShort.map((w) => `${w.name} ${Math.round(w.share * 100)}%`).join(', ')}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </Section>
      )}
      {vm.production.length > 0 && (
        <Section id="production" title={t.production}>
          <table class="table report__production">
            <thead>
              <tr>
                <th scope="col">
                  <span class="visually-hidden">Resource</span>
                </th>
                <th scope="col">{t.productionRow}</th>
                <th scope="col">{t.productionBefore}</th>
              </tr>
            </thead>
            <tbody>
              {vm.production.map((p) => (
                <tr key={p.resource}>
                  <th scope="row">{resourceLabel[p.resource]}</th>
                  <td class="num">
                    <Num value={p.now} />
                  </td>
                  <td class="num">{p.before === null ? '' : <Num value={p.before} />}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <Lines lines={vm.producers} />
        </Section>
      )}
      {vm.warnings.length > 0 && (
        <Section id="warnings" title={t.warnings}>
          <Lines lines={vm.warnings} />
        </Section>
      )}
      {vm.steady !== null && (
        <Section id="steady" title={t.steady}>
          {vm.steady.pass ? (
            <p>{t.steadyPass(vm.steady.counter, vm.steady.target)}</p>
          ) : (
            <p>
              {t.steadyFail} {vm.steady.failed.map((f) => uiText.steady[f] ?? f).join('; ')}.
            </p>
          )}
        </Section>
      )}
      <Section id="other" title={t.other}>
        {vm.other.length === 0 ? <p class="hint">{t.nothing}</p> : <Lines lines={vm.other} />}
      </Section>
      <footer class="overlay__actions">
        {outcome !== null && (
          <button type="button" class="button" onClick={onLedger}>
            {t.ledger}
          </button>
        )}
        {ended ? (
          <button type="button" class="button button--primary" onClick={onTitle} data-autofocus>
            {t.toTitle}
          </button>
        ) : (
          <button type="button" class="button button--primary" onClick={onContinue} data-autofocus>
            {t.continue}
          </button>
        )}
      </footer>
    </article>
  );
}
